/**
 * AI Extraction Demo Component
 * Key investor attraction - shows AI-powered invoice extraction
 *
 * Animation sequence:
 * 1. Upload zone (initial state)
 * 2. Processing animation (3 seconds)
 * 3. Fields populate with typing effect
 * 4. Confidence score fills
 * 5. Time saved banner appears
 */
import { useState, useEffect, useCallback } from 'react';
import { Upload, CheckCircle, Clock, AlertCircle, FileText, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatTimeSaved } from '../../lib/currency-br';

interface ExtractedField {
  label: string;
  value: string;
  confidence: number;
  delay: number;
}

interface AIExtractionDemoProps {
  autoPlay?: boolean;
  onComplete?: () => void;
  className?: string;
}

type DemoState = 'initial' | 'processing' | 'completed';

const SAMPLE_FIELDS: ExtractedField[] = [
  { label: 'Fornecedor', value: 'ABC Distribuidora de Alimentos LTDA', confidence: 98, delay: 0 },
  { label: 'CNPJ', value: '12.345.678/0001-99', confidence: 99, delay: 400 },
  { label: 'Valor Total', value: 'R$ 15.432,50', confidence: 97, delay: 800 },
  { label: 'Data Emissão', value: '15/01/2026', confidence: 99, delay: 1200 },
  { label: 'Vencimento', value: '14/02/2026', confidence: 95, delay: 1600 },
  { label: 'ICMS', value: 'R$ 2.778,85 (18%)', confidence: 94, delay: 2000 },
  { label: 'PIS/COFINS', value: 'R$ 1.387,50 (9%)', confidence: 93, delay: 2400 },
];

const TOTAL_CONFIDENCE = 96;
const MANUAL_TIME_SECONDS = 300; // 5 minutes
const AI_TIME_SECONDS = 30;
const TIME_SAVED_SECONDS = MANUAL_TIME_SECONDS - AI_TIME_SECONDS;

// Animated text component with typing effect
function AnimatedText({
  value,
  delay,
  onComplete
}: {
  value: string;
  delay: number;
  onComplete?: () => void;
}) {
  const [displayed, setDisplayed] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      let index = 0;
      const interval = setInterval(() => {
        setDisplayed(value.slice(0, index + 1));
        index++;
        if (index >= value.length) {
          clearInterval(interval);
          setShowCursor(false);
          onComplete?.();
        }
      }, 25);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timer);
  }, [value, delay, onComplete]);

  return (
    <span className="font-medium text-gray-900">
      {displayed}
      {showCursor && displayed.length < value.length && (
        <span className="animate-pulse text-teal-500">|</span>
      )}
    </span>
  );
}

// Confidence indicator component
function ConfidenceIndicator({ confidence, show }: { confidence: number; show: boolean }) {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(() => setWidth(confidence), 100);
    return () => clearTimeout(timer);
  }, [show, confidence]);

  const getColor = (conf: number) => {
    if (conf >= 95) return 'bg-emerald-500';
    if (conf >= 90) return 'bg-teal-500';
    if (conf >= 80) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="flex items-center gap-1">
      <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-700 ease-out',
            getColor(confidence)
          )}
          style={{ width: `${width}%` }}
        />
      </div>
      <span className={cn(
        'text-xs font-medium',
        confidence >= 95 ? 'text-emerald-600' :
        confidence >= 90 ? 'text-teal-600' :
        confidence >= 80 ? 'text-yellow-600' : 'text-red-600'
      )}>
        {confidence}%
      </span>
    </div>
  );
}

// Extracted field row
function ExtractedFieldRow({
  field,
  show,
  showConfidence
}: {
  field: ExtractedField;
  show: boolean;
  showConfidence: boolean;
}) {
  const [completed, setCompleted] = useState(false);

  if (!show) return null;

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500 min-w-[120px]">{field.label}</span>
      <div className="flex items-center gap-3 flex-1 justify-end">
        <AnimatedText
          value={field.value}
          delay={field.delay}
          onComplete={() => setCompleted(true)}
        />
        {completed && (
          <div className="flex items-center gap-2">
            <ConfidenceIndicator confidence={field.confidence} show={showConfidence} />
            <CheckCircle className="h-4 w-4 text-emerald-500 animate-in fade-in duration-300" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function AIExtractionDemo({
  autoPlay = false,
  onComplete,
  className
}: AIExtractionDemoProps) {
  const [state, setState] = useState<DemoState>('initial');
  const [progress, setProgress] = useState(0);
  const [showFields, setShowFields] = useState(false);
  const [showConfidence, setShowConfidence] = useState(false);
  const [showTimeSaved, setShowTimeSaved] = useState(false);
  const [overallConfidence, setOverallConfidence] = useState(0);

  const startDemo = useCallback(() => {
    setState('processing');
    setProgress(0);
    setShowFields(false);
    setShowConfidence(false);
    setShowTimeSaved(false);
    setOverallConfidence(0);

    // Progress animation (3 seconds)
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 60);

    // After processing, show fields
    setTimeout(() => {
      setState('completed');
      setShowFields(true);
    }, 3000);

    // Show confidence indicators
    setTimeout(() => {
      setShowConfidence(true);
    }, 5500);

    // Show overall confidence
    setTimeout(() => {
      const confidenceInterval = setInterval(() => {
        setOverallConfidence(prev => {
          if (prev >= TOTAL_CONFIDENCE) {
            clearInterval(confidenceInterval);
            return TOTAL_CONFIDENCE;
          }
          return prev + 2;
        });
      }, 20);
    }, 6000);

    // Show time saved
    setTimeout(() => {
      setShowTimeSaved(true);
      onComplete?.();
    }, 7000);
  }, [onComplete]);

  // Auto-play on mount if enabled
  useEffect(() => {
    if (!autoPlay) return;
    const timer = setTimeout(startDemo, 500);
    return () => clearTimeout(timer);
  }, [autoPlay, startDemo]);

  const resetDemo = () => {
    setState('initial');
    setProgress(0);
    setShowFields(false);
    setShowConfidence(false);
    setShowTimeSaved(false);
    setOverallConfidence(0);
  };

  const getProgressText = () => {
    if (progress < 30) return 'Lendo documento...';
    if (progress < 60) return 'Extraindo campos...';
    if (progress < 90) return 'Validando dados...';
    return 'Finalizando...';
  };

  return (
    <div className={cn('rounded-xl bg-white shadow-lg overflow-hidden', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-teal-600 to-teal-500">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-white" />
          <h3 className="font-semibold text-white">Extração Inteligente com IA</h3>
        </div>
        {state === 'completed' && (
          <button
            onClick={resetDemo}
            className="text-xs text-white/80 hover:text-white underline"
          >
            Reiniciar Demo
          </button>
        )}
      </div>

      <div className="p-6 space-y-6">
        {/* Initial State: Upload Zone */}
        {state === 'initial' && (
          <div
            onClick={startDemo}
            className="border-2 border-dashed border-gray-300 hover:border-teal-500
                       transition-colors rounded-xl p-8 cursor-pointer text-center
                       hover:bg-teal-50/50"
          >
            <Upload className="h-12 w-12 text-teal-500 mx-auto" />
            <p className="mt-4 text-gray-600 font-medium">
              Arraste um arquivo aqui
            </p>
            <p className="text-gray-400">ou clique para enviar</p>
            <p className="mt-2 text-sm text-gray-400">
              PDF, JPG, PNG (máx. 10MB)
            </p>
            <button
              className="mt-4 px-6 py-2 bg-teal-600 text-white rounded-lg
                         hover:bg-teal-700 transition-colors font-medium"
            >
              Iniciar Demo
            </button>
          </div>
        )}

        {/* Processing State */}
        {state === 'processing' && (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4">
              <FileText className="h-8 w-8 text-teal-500 animate-pulse" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">
                    nf-e-000001234.pdf
                  </span>
                  <span className="text-sm text-gray-500">{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-teal-400 to-teal-600 h-2 rounded-full
                               transition-all duration-100 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
            <p className="text-center text-gray-500 animate-pulse">
              {getProgressText()}
            </p>
          </div>
        )}

        {/* Completed State: Extracted Fields */}
        {state === 'completed' && (
          <>
            {/* Success message */}
            <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
              <span className="text-sm font-medium text-emerald-700">
                Documento processado com sucesso!
              </span>
            </div>

            {/* Extracted fields */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Campos Extraídos:
              </h4>
              <div className="border rounded-lg divide-y divide-gray-100">
                <div className="px-4">
                  {SAMPLE_FIELDS.map((field) => (
                    <ExtractedFieldRow
                      key={field.label}
                      field={field}
                      show={showFields}
                      showConfidence={showConfidence}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Overall confidence */}
            {showConfidence && (
              <div className="flex items-center gap-3 animate-in fade-in duration-500">
                <span className="text-sm text-gray-500">Confiança da IA:</span>
                <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500
                               rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${overallConfidence}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-emerald-600 min-w-[40px]">
                  {overallConfidence}%
                </span>
              </div>
            )}

            {/* Action buttons */}
            {showConfidence && (
              <div className="flex gap-3 animate-in slide-in-from-bottom duration-300">
                <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5
                                   bg-teal-600 text-white rounded-lg hover:bg-teal-700
                                   transition-colors font-medium">
                  <CheckCircle className="h-4 w-4" />
                  Confirmar
                </button>
                <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5
                                   border border-gray-300 text-gray-700 rounded-lg
                                   hover:bg-gray-50 transition-colors font-medium">
                  <AlertCircle className="h-4 w-4" />
                  Corrigir
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Time Saved Banner */}
      {showTimeSaved && (
        <div className="flex items-center justify-center gap-3 py-4 px-6
                        bg-gradient-to-r from-teal-50 to-emerald-50 border-t
                        animate-in slide-in-from-bottom duration-500">
          <Clock className="h-5 w-5 text-teal-600" />
          <div className="text-center">
            <span className="text-sm text-teal-700">
              Tempo economizado: <strong>{formatTimeSaved(TIME_SAVED_SECONDS)}</strong>
            </span>
            <span className="text-xs text-gray-500 block">
              (Manual: 5 min → IA: 30 seg)
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
